import { useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  onTimeUpdate?: (currentTime: number) => void;
}

/**
 * Optimized video player component with visibility-based playback
 */
export default function VideoPlayer({
  src,
  poster,
  className = '',
  autoPlay = true,
  loop = true,
  muted = true,
  controls = true,
  onPlay,
  onPause,
  onEnd,
  onTimeUpdate
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Use intersection observer to detect when video is in viewport
  const { ref: viewRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });
  
  // Handle video visibility changes
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    if (inView && autoPlay) {
      // Pause any other videos and audio that might be playing
      document.querySelectorAll('video, audio').forEach(media => {
        const mediaElement = media as HTMLVideoElement | HTMLAudioElement;
        if (mediaElement !== videoElement && !mediaElement.paused) {
          mediaElement.pause();
        }
      });
      
      // Add user interaction detection for autoplay
      const attemptPlay = () => {
        videoElement.muted = true; // Ensure muted for autoplay policy
        videoElement.play().catch(() => {
          // Silently handle autoplay failure - this is expected browser behavior
          // User can manually play if they want to hear video
          if (process.env.NODE_ENV === 'development') {
            console.warn("Video autoplay blocked by browser policy");
          }
        });
      };
      
      attemptPlay();
    } else if (!inView) {
      videoElement.pause();
    }
  }, [inView, autoPlay]);
  
  // Handle time updates
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !onTimeUpdate) return;
    
    const handleTimeUpdate = () => {
      onTimeUpdate(videoElement.currentTime);
    };
    
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [onTimeUpdate]);
  
  // Handle play/pause/end events
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    const handlePlay = () => onPlay && onPlay();
    const handlePause = () => onPause && onPause();
    const handleEnded = () => onEnd && onEnd();
    
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    
    return () => {
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [onPlay, onPause, onEnd]);
  
  return (
    <div ref={viewRef} className="w-full h-full">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className={`w-full h-full object-cover ${className}`}
        loop={loop}
        muted={muted}
        playsInline
        preload="metadata"
        autoPlay={autoPlay && muted}
        controls={controls}
      />
    </div>
  );
}
